import React, { useMemo } from 'react';
import { Card, CardHeader } from '../ui';

const DAY_MS = 24 * 60 * 60 * 1000;
const VOID_STATUSES = new Set(['REJECTED', 'CANCELLED', 'EXPIRED']);

function computeBreakdown(orders) {
  const cutoff = Date.now() - 30 * DAY_MS;
  const recent = orders.filter(
    (o) => !VOID_STATUSES.has(o.status) && new Date(o.createdAt).getTime() >= cutoff,
  );

  const buyCount = recent.filter((o) => o.side === 'BUY').length;
  const sellCount = recent.filter((o) => o.side === 'SELL').length;
  const total = buyCount + sellCount;

  const symbolCounts = new Map();
  recent.forEach((o) => {
    if (!o.symbol) return;
    symbolCounts.set(o.symbol, (symbolCounts.get(o.symbol) || 0) + 1);
  });
  const topInstruments = Array.from(symbolCounts.entries())
    .map(([symbol, trades]) => ({ symbol, trades }))
    .sort((a, b) => b.trades - a.trades)
    .slice(0, 5);

  return {
    buyPct: total > 0 ? (buyCount / total) * 100 : 0,
    sellPct: total > 0 ? (sellCount / total) * 100 : 0,
    topInstruments,
    hasData: recent.length > 0,
  };
}

const OrdersBreakdownCard = ({ orders, loading }) => {
  const { buyPct, sellPct, topInstruments, hasData } = useMemo(
    () => computeBreakdown(orders || []),
    [orders],
  );
  const maxTrades = topInstruments[0]?.trades || 1;

  return (
    <Card pad={false}>
      <CardHeader title="Buy / sell & top instruments" description="Last 30 days" />
      <div className="p-6">
        {loading ? (
          <div className="h-32 flex items-center justify-center">
            <p className="text-sm text-ink-300 animate-pulse">Loading…</p>
          </div>
        ) : !hasData ? (
          <div className="h-32 flex items-center justify-center">
            <p className="text-sm text-ink-400">No orders in this period</p>
          </div>
        ) : (
          <>
            <div className="h-2.5 rounded-full overflow-hidden flex bg-ink-100">
              <div className="bg-accent-500" style={{ width: `${buyPct}%` }} />
              <div className="bg-ink-300" style={{ width: `${sellPct}%` }} />
            </div>
            <div className="mt-2.5 flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-ink-600">
                <span className="h-2 w-2 rounded-full bg-accent-500" />
                Buy {buyPct.toFixed(0)}%
              </span>
              <span className="flex items-center gap-1.5 text-ink-600">
                <span className="h-2 w-2 rounded-full bg-ink-300" />
                Sell {sellPct.toFixed(0)}%
              </span>
            </div>

            {topInstruments.length > 0 && (
              <>
                <p className="mt-6 text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-3">
                  Most-traded instruments
                </p>
                <ul className="space-y-3">
                  {topInstruments.map((inst) => (
                    <li key={inst.symbol}>
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-sm font-semibold text-ink-900">{inst.symbol}</span>
                        <span className="text-xs text-ink-500 tnum shrink-0">
                          {inst.trades.toLocaleString('en-US')} trades
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent-400"
                          style={{ width: `${(inst.trades / maxTrades) * 100}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default OrdersBreakdownCard;
